import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { LEAD_STATUSES, QUALIFICATIONS, STATUS_STYLES, QUAL_STYLES, Badge, leadName, fmtDateTime } from "@/lib/leads";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Image as ImageIcon } from "lucide-react";

const ALL = "__all";

export default function LeadsList() {
  const [leads, setLeads] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(ALL);
  const [qual, setQual] = useState(ALL);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/revival-leads", { params: { search, status: status === ALL ? "" : status, qualification: qual === ALL ? "" : qual } })
      .then((r) => setLeads(r.data)).catch(() => setLeads([]));
  }, [search, status, qual]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <AdminLayout title="Revival Leads">
      <div className="flex flex-col lg:flex-row gap-3 mb-6">
        <div className="relative w-full lg:w-96">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input data-testid="leads-search" placeholder="Search name, email, phone, postal code, lead #" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-11 rounded-sm bg-white" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="leads-status-filter" className="h-11 rounded-sm bg-white lg:w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All sales statuses</SelectItem>
            {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={qual} onValueChange={setQual}>
          <SelectTrigger data-testid="leads-qual-filter" className="h-11 rounded-sm bg-white lg:w-64"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All qualifications</SelectItem>
            {QUALIFICATIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead>
            <tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-[0.12em] text-gray-500">
              {["Lead #", "Customer", "Furniture", "Colour goal", "Timeline", "Photos", "Qualification", "Status", "Source", "Received"].map((h) => (
                <th key={h} className="px-4 py-3 font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads === null && <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-400">Loading...</td></tr>}
            {leads?.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-16 text-center text-gray-400" data-testid="no-leads-message">
                No leads match. New landing-page submissions appear here the moment they come in.
              </td></tr>
            )}
            {leads?.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.lead_number}`} onClick={() => navigate(`/admin/leads/${l.id}`)} className="border-b border-gray-100 hover:bg-[#FAF7F2] cursor-pointer transition-colors duration-150">
                <td className="px-4 py-3.5 font-semibold whitespace-nowrap">{l.lead_number}</td>
                <td className="px-4 py-3.5 whitespace-nowrap">
                  <p className="font-medium">{leadName(l)}</p>
                  <p className="text-xs text-gray-400">{l.customer?.email}</p>
                </td>
                <td className="px-4 py-3.5 max-w-[200px] truncate">{l.furniture_types?.join(", ")}</td>
                <td className="px-4 py-3.5 max-w-[220px] truncate text-gray-600">{l.colour_goal_label}</td>
                <td className="px-4 py-3.5 whitespace-nowrap text-gray-600">{l.timeline_label}</td>
                <td className="px-4 py-3.5"><span className="inline-flex items-center gap-1 text-gray-500"><ImageIcon size={13} /> {l.photo_count}</span></td>
                <td className="px-4 py-3.5"><Badge value={l.qualification} styles={QUAL_STYLES} /></td>
                <td className="px-4 py-3.5"><Badge value={l.status} styles={STATUS_STYLES} /></td>
                <td className="px-4 py-3.5 text-gray-500 whitespace-nowrap text-xs">{l.utm?.utm_source || "direct"}{l.utm?.utm_campaign ? ` · ${l.utm.utm_campaign}` : ""}</td>
                <td className="px-4 py-3.5 text-gray-500 whitespace-nowrap">{fmtDateTime(l.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
